// Drawer wrapper for an opened request. Hosts the header (id + status +
// SLA + action buttons), the meta row, the 5 tabs, and the return-banner.
// Uses the shared ui/Drawer for animation + focus trap; overlays the
// list page (which is `position:relative`).

import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';
import {
  type CreateRussiaRequestFromParentInput,
  type RussiaRequest,
  type TransitionKey,
} from '@magimp/shared';
import { Drawer } from '../../../ui/index.js';
import { useAuthStore } from '../../../stores/auth.js';
import { useToastStore } from '../../../stores/toast.js';
import { useRequestsStore } from '../store.js';
import { useDuplicateRequest, useRequest, useRequestSnapshots, useTransition } from '../queries.js';
import {
  createRussiaRequestFromParent,
  getRussiaRequestFromParent,
} from '../../../api/russia-requests/index.js';
import { useAutoMarkNotificationsViewed } from '../../notifications/index.js';
import { wireRequestToView } from '../adapter.js';
import {
  allowedActions,
  canCreateRussiaRequestFromParent,
  canDuplicateRequest,
  canPickWinner,
  detectRoleKind,
  hasParentCargo,
  type TransitionAction,
} from '../role.js';
import { errorMessageKey } from '../../../api/errors.js';
import { formatSLA } from '../format.js';
import type { RequestView, UserRef } from '../types.js';
import { ItemsTab } from './tabs/ItemsTab.js';
import { CarriersTab } from './tabs/CarriersTab.js';
import { ChatTab } from './tabs/ChatTab.js';
import { AuditTab } from './tabs/AuditTab.js';
import { FilesTab } from './tabs/FilesTab.js';
import { ArchiveTab } from './tabs/ArchiveTab.js';
import { ArchiveSnapshotView } from './tabs/ArchiveSnapshotView.js';
import { useRequestUrlState, type RequestDrawerTabId } from '../useRequestUrlState.js';
import { RequestDrawerHeader } from './RequestDrawerHeader.js';
import { RequestDrawerTabs } from './RequestDrawerTabs.js';
import { RequestDrawerReturnBanner } from './RequestDrawerReturnBanner.js';
import { RequestNextStepHint } from './RequestNextStepHint.js';
import { RequestDrawerStickyActionBar } from './RequestDrawerStickyActionBar.js';
import { RequestDrawerUnavailableStub } from './RequestDrawerUnavailableStub.js';
import { RequestDrawerModalHost } from './RequestDrawerModalHost.js';
import { RussiaRateCreateModal } from './RussiaRateCreateModal.js';

export interface RequestDrawerProps {
  requestId: string;
  narrow: boolean;
  onClose: () => void;
}

export function RequestDrawer({ requestId, narrow, onClose }: RequestDrawerProps) {
  const { t } = useTranslation('requests');
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);

  const { modal, setModal } = useRequestsStore(
    useShallow((s) => ({
      modal: s.modal,
      setModal: s.setModal,
    })),
  );
  const requestQuery = useRequest(requestId);
  // WHY (#2580): one snapshot-list query owns both the tab count and archive
  //   body, preventing a second fetch when the user opens the tab.
  const snapshotsQuery = useRequestSnapshots(requestId);
  const request = useMemo<RequestView | null>(
    () => (requestQuery.data ? wireRequestToView(requestQuery.data) : null),
    [requestQuery.data],
  );
  const transitionMutation = useTransition(requestId);
  const urlState = useRequestUrlState();
  const tab = urlState.currentTab;
  // INVARIANT (#1478, updated #2102): the Files tab is available for all
  //   China request kinds incl. SIMPLE. No kind-based redirect. The
  //   Codex F-001 loading-window note still applies: effectiveTab does not
  //   collapse while request is null, so `useAutoMarkNotificationsViewed`
  //   receives the correct tab during the loading window.
  const effectiveTab: RequestDrawerTabId = tab;
  // INVARIANT (#2140 FR-4/AC-3): while a return snapshot is open on the
  //   Archive tab, the drawer enters a read-only TAKEOVER — only the frozen
  //   `ArchiveSnapshotView` renders; the live header/hint/tab-strip/return-
  //   banner/sticky-action-bar are all suppressed so no live transition,
  //   edit, carrier, files, chat, audit, duplicate, or Russia action is
  //   reachable from frozen state. Tab-gated so a stray `snapshotId` param on
  //   any non-archive tab is ignored.
  const snapshotId = effectiveTab === 'archive' ? urlState.currentSnapshotId : null;
  // INVARIANT: hooks must be called in stable order on every render. Both
  //   navigate + toast are consumed by the #707 Russia-rate handler, which
  //   only runs after the request has loaded — but the hooks themselves
  //   must be subscribed unconditionally, ahead of the `!request` early
  //   return below.
  const navigate = useNavigate();
  const showToast = useToastStore((s) => s.show);
  const qc = useQueryClient();
  const russiaRateMutation = useMutation<
    RussiaRequest,
    Error,
    { parentRequestId: string; input?: CreateRussiaRequestFromParentInput }
  >({
    mutationFn: ({ parentRequestId, input = {} }) =>
      createRussiaRequestFromParent(parentRequestId, input),
    // WHY (#1753): the china drawer must not import the russia-requests feature
    //   (boundary). Replicate russiaRequestsKeys.all + .detail(id) invalidation
    //   inline — the key shapes are the documented russia invariant: the
    //   ['russia-requests'] list prefix and the ['russia-request', id] detail.
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: ['russia-requests'] });
      void qc.invalidateQueries({ queryKey: ['russia-request', row.id] });
    },
  });
  // INVARIANT (#1180): the confirm flow has three local UI states:
  //   - `russiaRateLookupPending`: the imperative `GET /from-parent/:id`
  //     probe is in flight. Disables the button so a second click cannot
  //     stack lookups (which would race for the modal).
  //   - `russiaRateLookupErrored`: lookup rejected (non-no-child error).
  //     Drives the tooltip back to the `error` copy and the toast path,
  //     and clears on the next click.
  //   - `russiaRateConfirmOpen`: the no-child path opened the required-
  //     address modal (#1579). Cancel/Escape/backdrop must close without
  //     invoking the create mutation; only Confirm (with a valid trimmed
  //     address) calls it.
  const [russiaRateLookupPending, setRussiaRateLookupPending] = useState(false);
  const [russiaRateLookupErrored, setRussiaRateLookupErrored] = useState(false);
  const [russiaRateConfirmOpen, setRussiaRateConfirmOpen] = useState(false);
  // INVARIANT (#2119): the duplicate mutation hook is subscribed
  //   unconditionally, ahead of the `!request` early return below, so the
  //   hook order stays stable across renders.
  const duplicateMutation = useDuplicateRequest();

  // INVARIANT (#1072): drawer + tab open auto-marks notification
  //   aggregates viewed. Responsible-status fires once per drawer mount,
  //   carriers fires when the carriers tab is active (rates/rejections/
  //   insurance bundle), chat fires when chat is active. Hook ordering
  //   matters — call before any early returns below so hooks order
  //   stays stable across renders.
  useAutoMarkNotificationsViewed({
    entityType: 'CHINA_REQUEST',
    entityId: requestId,
    currentTab: effectiveTab,
  });

  // INVARIANT: a single-branch
  //   `if (!request || requestQuery.isError) return null;`
  // would conflate three states — cold-path 404, warm-cache 404, and
  // normal initial loading. Two ordered branches preserve the security
  // invariant (warm-path 404 must not leak stale data — react-query
  // retains `data` after a failed refetch) AND the loading UX (no false
  // unavailable-stub flash on every drawer open).
  // - First: `isError` → render the isolated `<RequestDrawerUnavailableStub>`
  //   which does not import `RequestView` and cannot reach the cached data.
  //   Both cold-path and warm-cache 404 land here.
  // - Second: `!request` → render `null`, preserving the "blank panel
  //   until the query settles" UX.
  // The stub's X / Esc / backdrop dismissal share `closeUnavailable` so
  // all three channels rewrite the URL via `replace` — otherwise they
  // would leave a broken `?openId=` entry behind in history.
  const closeUnavailable = () => urlState.closeRequest({ replace: true });
  if (requestQuery.isError) {
    return (
      <Drawer onClose={closeUnavailable} narrow={narrow} width={880}>
        <RequestDrawerUnavailableStub requestId={requestId} onClose={closeUnavailable} />
      </Drawer>
    );
  }
  if (!request) return null;
  const r = request;

  const role = detectRoleKind(user);
  const me: UserRef = user
    ? { id: user.id, name: user.name, role }
    : { id: 'unknown', name: t('common.guest'), role: 'manager' };

  // INVARIANT (#445): party-aware gating — backend chokepoint is
  // authoritative; this client gate just hides buttons the backend
  // would 403, so a logist_cn on scope='all' doesn't see actions on
  // foreign rows. `actorUserId: null` falls through to a no-action
  // result for non-admin actors (defensive against a transient null
  // user during hydration). The admin (`permissions === 'ALL'`)
  // bypass is derived inside the helper, not threaded through the
  // ctx, so the client-side predicate stays single-sourced.
  const party = {
    actorUserId: user?.id ?? null,
    managerId: r.managerId,
    logistId: r.logistId,
  };
  const hasAnsweredCarrier = r.carriers.some((c) => c.state === 'answered');
  const actions = allowedActions(r.status, permissions, party, { hasAnsweredCarrier });
  // #1595: the next-step hint's pick-winner action reuses the SAME gate as
  //   the carriers-tab CTA (`canPickWinner`) — IN_WORK/PROCESSED, no winner
  //   yet, `requests.manage` + manager party (admin/`ALL` bypass). It only
  //   navigates to the carriers tab; the winner is still picked there.
  const pickWinnerAllowed = canPickWinner(permissions, r.status, r.winnerBidId != null, party);

  const onAction = (a: TransitionAction) => {
    // #1164: a disabled action is rendered for the tooltip only — the
    //   click is a no-op so the user is not silently routed to a
    //   modal-less mutation that the backend would 409 anyway.
    if (a.disabled) return;
    if (a.key === 'refuse') {
      setModal({ type: 'refuse', requestId: r.id });
      return;
    }
    if (a.key === 'reopen') {
      setModal({ type: 'reopen', requestId: r.id });
      return;
    }
    // INVARIANT (#2139 FR-2): `cannotTransport` also carries `needsReason`,
    //   so it MUST route to its dedicated modal BEFORE the generic
    //   `return`-modal fallback — otherwise it would submit key:'return'.
    if (a.key === 'cannotTransport') {
      setModal({ type: 'cannotTransport', requestId: r.id });
      return;
    }
    if (a.needsReason) {
      setModal({ type: 'return', requestId: r.id });
      return;
    }
    if (a.needsConfirm && a.key === 'close') {
      setModal({ type: 'close', requestId: r.id });
      return;
    }
    if (a.needsConfirm && a.key === 'success') {
      setModal({ type: 'success', requestId: r.id });
      return;
    }
    transitionMutation.mutate(
      { key: a.key },
      {
        // #1164: surface backend gate-failures (e.g.
        //   ANSWERED_CARRIER_REQUIRED on a fresh race between render
        //   and click) as a localized toast instead of letting the
        //   mutation reject silently.
        onError: (err) => {
          showToast({ tone: 'error', text: t(errorMessageKey(err) as never) });
        },
      },
    );
  };

  const sla = formatSLA(
    {
      status: r.status,
      slaDeadline: r.slaDeadline,
      inWorkAt: r.inWorkAt,
      closedAt: r.closedAt,
      processedAt: r.processedAt,
    },
    Date.now(),
  );

  // #2119: owner-only (or admin) duplicate affordance, any source status.
  //   Single-flight: a fresh `clientToken` is minted per click and the
  //   button is disabled while the mutation is in flight, so a double-click
  //   cannot mint two rows. On success the drawer opens the new DRAFT.
  const duplicateVisible = canDuplicateRequest(permissions, party);
  const onDuplicateClick = () => {
    if (duplicateMutation.isPending) return;
    duplicateMutation.mutate(
      { id: r.id, clientToken: crypto.randomUUID() },
      {
        onSuccess: (req) => urlState.openRequest(req.id),
        onError: (err) => {
          showToast({ tone: 'error', text: t(errorMessageKey(err) as never) });
        },
      },
    );
  };

  const russiaRateVisible = canCreateRussiaRequestFromParent(permissions);
  const russiaRateHasCargo = hasParentCargo({
    itemCount: r.items.length,
    boxCount: r.boxes?.length,
  });
  // INVARIANT (#707, refined #1180): tooltip selection is informational.
  //   The button is disabled only when cargo is empty. Idempotency is
  //   enforced by the backend (`parentRequestId @unique`); the lookup
  //   probe decides whether to show the create-confirmation modal.
  //   `tooltip.openExisting` is reserved for post-success copy and is
  //   not driven from drawer state because there is no pre-click probe.
  const russiaRateBusy = russiaRateLookupPending || russiaRateMutation.isPending;
  const russiaRateTooltipKey =
    russiaRateMutation.isError || russiaRateLookupErrored
      ? 'actions.russiaRate.tooltip.error'
      : russiaRateBusy
        ? 'actions.russiaRate.tooltip.creating'
        : russiaRateHasCargo
          ? 'actions.russiaRate.tooltip.create'
          : 'actions.russiaRate.tooltip.disabledEmptyCargo';
  const onRussiaRateClick = async () => {
    if (russiaRateBusy || russiaRateConfirmOpen) return;
    setRussiaRateLookupErrored(false);
    setRussiaRateLookupPending(true);
    try {
      const existing = await getRussiaRequestFromParent(r.id);
      if (existing) {
        navigate(`/russia-rates/${encodeURIComponent(existing.id)}`);
        return;
      }
      setRussiaRateConfirmOpen(true);
    } catch {
      // INVARIANT (#1180): only the typed no-child 404 collapses to
      //   `null`; every other failure (network, 403 ownership mismatch,
      //   500) lands here and surfaces through the standard toast path.
      setRussiaRateLookupErrored(true);
      showToast({ tone: 'error', text: t('actions.russiaRate.tooltip.error') });
    } finally {
      setRussiaRateLookupPending(false);
    }
  };
  // INVARIANT (#1579): the new snapshot carries the REQUIRED delivery
  //   address the manager typed in the modal — never the '—' placeholder.
  //   The modal already trimmed + validated it (non-empty, max 2000).
  const onRussiaRateConfirm = (endAddress: string) => {
    if (russiaRateMutation.isPending) return;
    setRussiaRateConfirmOpen(false);
    russiaRateMutation.mutate(
      { parentRequestId: r.id, input: { endAddress } },
      {
        onSuccess: (row) => navigate(`/russia-rates/${encodeURIComponent(row.id)}`),
        onError: () => {
          showToast({ tone: 'error', text: t('actions.russiaRate.tooltip.error') });
        },
      },
    );
  };

  return (
    <Drawer onClose={onClose} narrow={narrow} width={880}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minWidth: 0,
          background: 'var(--bg-1)',
        }}
      >
        {snapshotId !== null ? (
          <ArchiveSnapshotView
            request={r}
            snapshotId={snapshotId}
            onBack={() => urlState.clearSnapshot()}
            onClose={onClose}
          />
        ) : (
          <>
            <RequestDrawerHeader
              r={r}
              actions={actions}
              sla={sla}
              onAction={onAction}
              onClose={onClose}
              narrow={narrow}
              duplicate={{
                visible: duplicateVisible,
                pending: duplicateMutation.isPending,
                onClick: onDuplicateClick,
              }}
              russiaRate={{
                visible: russiaRateVisible,
                disabled: !russiaRateHasCargo,
                pending: russiaRateBusy,
                tooltip: t(russiaRateTooltipKey) as string,
                // INVARIANT (#1180): `onClick` is the async lookup-first
                //   entry point. The header button binds it synchronously;
                //   the promise is intentionally fire-and-forget there
                //   (errors are routed via the toast + tooltip state
                //   handled inside the handler).
                onClick: () => {
                  void onRussiaRateClick();
                },
              }}
            />
            <RequestNextStepHint
              surface="request"
              status={r.status}
              hasAnsweredCarrier={hasAnsweredCarrier}
              hasWinner={r.winnerBidId != null}
              canPickWinner={pickWinnerAllowed}
              onPickWinner={() => urlState.switchTab('carriers')}
            />
            <RequestDrawerTabs
              r={r}
              tab={effectiveTab}
              setTab={(id) => urlState.switchTab(id)}
              narrow={narrow}
              archiveCount={snapshotsQuery.data?.length ?? 0}
            />
            <RequestDrawerReturnBanner r={r} />
            <div
              id={`request-drawer-tabpanel-${effectiveTab}`}
              role="tabpanel"
              aria-labelledby={`request-drawer-tab-${effectiveTab}`}
              style={{ flex: 1, overflow: 'auto', minHeight: 0 }}
            >
              {effectiveTab === 'items' && <ItemsTab request={r} me={me} />}
              {effectiveTab === 'carriers' && <CarriersTab request={r} me={me} />}
              {effectiveTab === 'chat' && <ChatTab request={r} me={me} />}
              {effectiveTab === 'audit' && <AuditTab request={r} />}
              {effectiveTab === 'files' && <FilesTab request={r} />}
              {effectiveTab === 'archive' && (
                <ArchiveTab
                  snapshots={snapshotsQuery.data}
                  isLoading={snapshotsQuery.isLoading}
                  isError={snapshotsQuery.isError}
                  onRetry={() => void snapshotsQuery.refetch()}
                />
              )}
            </div>
            {narrow && actions.length > 0 && (
              <RequestDrawerStickyActionBar actions={actions} onAction={onAction} />
            )}
          </>
        )}
      </div>

      {/* WHY (#2140 FR-4): snapshot takeover must not leave a live mutation
          modal reachable over frozen state — gate both hosts on snapshotId. */}
      {snapshotId === null && <RequestDrawerModalHost modal={modal} request={r} me={me} />}

      {snapshotId === null && russiaRateConfirmOpen && (
        <RussiaRateCreateModal
          onConfirm={onRussiaRateConfirm}
          onClose={() => setRussiaRateConfirmOpen(false)}
          busy={russiaRateMutation.isPending}
        />
      )}
    </Drawer>
  );
}

// Re-export TransitionKey type for tab consumers (avoid extra imports there).
export type { TransitionKey };
